import { AfterViewInit, Component, Inject, QueryList, ViewChildren, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { BASE_INDI_URL } from 'src/app/injection-tokens';
import { ActivatedRoute, Router } from '@angular/router';
import { FeedbackSettings } from 'src/app/general-components/feedback/feedback-settings';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { Profile } from 'src/app/general-components/profile/profile';
import { AuthService } from 'src/app/auth/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorResponse } from 'src/app/interfaces/bad-request';
import { finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Address, IndiCustomer } from '../indi-customer';
import { ApiResponse } from '../api-response';
import { APIContentLink, APIVerkooporder } from '../APIVerkooporders';
import { APIVerkoopordersToWrite } from '../APIVerkoopordersToWrite';
import { User } from '../user';
import { InfoForm, InvoiceDetailsForm, OrderlineForm, ShippingDetailsForm } from '../form-controls';
import { ManuallyCreatedRequest } from '../manually-created-request';
import { VerkoopordersCustomerPreferencesToWrite } from '../VerkoopordersCustomerPreferences';
import { CombinedData, NewSalesorderService } from './new-salesorder.service';
import { SHIPPING_METHOD_CODES } from './shipping-methods.const';
import { NewSalesorderInfoComponent } from './new-salesorder-info/new-salesorder-info.component';
import { NewSalesorderInvoiceDetailsComponent } from './new-salesorder-invoice-details/new-salesorder-invoice-details.component';
import { NewSalesorderShippingDetailsComponent } from './new-salesorder-shipping-details/new-salesorder-shipping-details.component';
import { NewSalesorderOrderlinesComponent } from './new-salesorder-orderlines/new-salesorder-orderlines.component';

@Component({
  selector: 'app-new-salesorder',
  templateUrl: './new-salesorder.component.html',
  styleUrls: ['./new-salesorder.component.scss'],
})
export class NewSalesorderComponent implements AfterViewInit, OnDestroy {
  @ViewChildren(NewSalesorderOrderlinesComponent) orderlinesComponentList!: QueryList<NewSalesorderOrderlinesComponent>;
  @ViewChildren(NewSalesorderShippingDetailsComponent)
  shippingComponentList!: QueryList<NewSalesorderShippingDetailsComponent>;
  @ViewChildren(NewSalesorderInvoiceDetailsComponent)
  invoiceComponentList!: QueryList<NewSalesorderInvoiceDetailsComponent>;
  @ViewChildren(NewSalesorderInfoComponent) infoComponentList!: QueryList<NewSalesorderInfoComponent>;

  subs = new Subscription();

  orderlinesComponent!: NewSalesorderOrderlinesComponent;
  shippingComponent!: NewSalesorderShippingDetailsComponent;
  invoiceComponent!: NewSalesorderInvoiceDetailsComponent;
  infoComponent!: NewSalesorderInfoComponent;

  profile!: Profile;
  APIVerkooporder: APIVerkooporder = { ticketId: 0, orderDetails: {} } as APIVerkooporder;
  indiCustomer: IndiCustomer = {} as IndiCustomer;
  users: User[] = [];
  apiResponse?: ApiResponse;
  form!: FormGroup;
  ids: number[] = [];
  ticketIds: number[] = [];
  contentLinks: APIContentLink[] = [];
  isLoading: boolean = true;
  manuallyCreatedIsLoading: boolean = false;
  isSent: boolean = false;
  orderError?: HttpErrorResponse;
  shippingAddresses: Address[] = [];
  nightShippingAddress!: Address;
  defaultInvoiceAddress!: Address;
  invoiceEmailAddress: string = '';
  icmNote: string = '';
  userUUIDPreference: string = '';

  feedbackSettings: FeedbackSettings = {
    identifier: 'generalFeedback',
    show: false,
    type: FeedbackType.error,
    message: '',
    closeButton: false,
  };

  viewInitialized = false;
  constructor(
    @Inject(BASE_INDI_URL) readonly baseIndiUrl: string,
    private route: ActivatedRoute,
    private router: Router,
    private service: NewSalesorderService,
    private fb: FormBuilder,
    private authService: AuthService
  ) {}

  get orderlines(): FormArray {
    return this.form.get('orderlines') as FormArray;
  }

  // Business unless explicitly a private (B2C) customer; false while the
  // customer is not loaded yet (the safer, consumer ATP routing).
  get isBusinessCustomer(): boolean {
    return !!this.indiCustomer.type && this.indiCustomer.type !== 'CT_PRIVATE';
  }

  get shippingDetails(): FormGroup {
    return this.form.get('shippingDetails') as FormGroup;
  }

  get invoiceDetails(): FormGroup {
    return this.form.get('invoiceDetails') as FormGroup;
  }

  get info(): FormGroup {
    return this.form.get('info') as FormGroup;
  }

  ngAfterViewInit(): void {
    this.subscribeToComponentsReady();
    this.profile = this.authService.getUserInfo();
    this.ids = this.route.snapshot.queryParamMap
      .getAll('id')
      .map(id => Number(id))
      .filter(id => !isNaN(id) && id !== 0);

    if (this.ids.length === 0) {
      this.updateFeedback('Geen geldige id gegeven', FeedbackType.error, false, true);
      this.isLoading = false;
      this.orderError = undefined;
      return;
    }
    this.service.getCombinedData(this.ids).subscribe(
      (combinedData: CombinedData) => {
        if (combinedData.APIVerkooporders.verkooporder.manuallyCreated) {
          this.updateFeedback(
            '(Een deel van) deze order is al handmatig aangemaakt!',
            FeedbackType.error,
            false,
            true,
            'Terug naar het overzicht'
          );
          this.isLoading = false;
          return;
        }

        this.icmNote = combinedData.ICMCustomerNotes.notes;

        this.indiCustomer = combinedData.indiCustomer;
        if (!this.customerIsSuitable(this.indiCustomer, this.icmNote)) {
          this.isLoading = false;
          return;
        }

        this.ticketIds = combinedData.APIVerkooporders.ticketIds;
        this.contentLinks = combinedData.APIVerkooporders.contentInfos;
        this.APIVerkooporder = combinedData.APIVerkooporders.verkooporder;
        this.users = combinedData.users;
        this.userUUIDPreference = combinedData.verkoopordersCustomerPreferences.preferredUserUUID ?? '';
        this.initializeAddresses();
        this.setupForm();
        this.isLoading = false;
      },
      (error: HttpErrorResponse) => {
        console.error('Failed to get salesorder information', error);
        const httpError = error.error as ErrorResponse;
        this.updateFeedback(
          'Er is iets fout gegaan bij het ophalen van de verkooporder' +
            (httpError.exception ? ': \n' + httpError.exception : ''),
          FeedbackType.error,
          false,
          true
        );
        this.isLoading = false;
      }
    );
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  customerIsSuitable(indiCustomer: IndiCustomer, customerNote: string): boolean {
    if (!indiCustomer.active) {
      this.updateFeedback(
        'Deze klant staat op inactief en daarom mogen er geen Verkooporders voor geboekt worden.' + //
          '\n\nKlantnotitie:\n' +
          customerNote,
        FeedbackType.error,
        false,
        true,
        'Terug naar het overzicht'
      );
      return false;
    }
    if (!indiCustomer.hasAllowInvoice) {
      this.updateFeedback(
        'Deze klant staat NIET op allow-invoice en daarom mogen er geen Verkooporders voor geboekt worden.' + //
          '\n\nKlantnotitie:\n' +
          customerNote,
        FeedbackType.error,
        false,
        true,
        'Terug naar het overzicht'
      );
      return false;
    }
    return true;
  }

  initializeAddresses(): void {
    this.defaultInvoiceAddress = this.indiCustomer.addresses.filter(
      address => address.type === 'atDefaultForInvoice' || address.type === 'atDefaultForInvoiceAndShipping'
    )[0];

    this.nightShippingAddress = this.indiCustomer.addresses.filter(address => address.nightShipping)[0];

    this.shippingAddresses = this.indiCustomer.addresses.filter(
      address => address.type !== 'atDefaultForInvoice' && address.type !== 'atForInvoice'
    );
  }

  createFormGroup(
    theOrderlines: FormArray<FormGroup<OrderlineForm>>,
    theShippingDetails: FormGroup<ShippingDetailsForm>,
    theInvoiceDetails: FormGroup<InvoiceDetailsForm>,
    theInfo: FormGroup<InfoForm>
  ): FormGroup {
    return this.fb.group({
      orderlines: theOrderlines,
      shippingDetails: theShippingDetails,
      invoiceDetails: theInvoiceDetails,
      info: theInfo,
    });
  }

  setupForm() {
    this.form = this.createFormGroup(
      this.createOrderlines(), //
      this.createShippingDetails(), //
      this.createInvoiceDetails(), //
      this.createInfo()
    );
  }

  createOrderlines(): FormArray<FormGroup<OrderlineForm>> {
    return NewSalesorderOrderlinesComponent.createOrderlinesFormArray(
      this.fb,
      this.APIVerkooporder.orderDetails?.orderlines ?? []
    );
  }

  createShippingDetails(): FormGroup<ShippingDetailsForm> {
    return NewSalesorderShippingDetailsComponent.createShippingDetails(
      this.fb,
      this.APIVerkooporder.orderDetails?.deliveryAddress ?? ({} as unknown as Address),
      this.userUUIDPreference
    );
  }

  createInvoiceDetails(): FormGroup<InvoiceDetailsForm> {
    return NewSalesorderInvoiceDetailsComponent.createInvoiceDetails(
      this.fb,
      this.APIVerkooporder.orderDetails?.customer.invoiceEmailAddress ?? ''
    );
  }

  createInfo(): FormGroup<InfoForm> {
    return NewSalesorderInfoComponent.createInfo(this.fb, this.APIVerkooporder.orderDetails?.customerRef ?? '');
  }

  handleInvoiceEmailChange(newEmail: string): void {
    this.invoiceEmailAddress = newEmail;
  }

  isFormValid(): boolean {
    if (!this.viewInitialized) {
      return false;
    }

    return (
      this.orderlinesComponent.valid &&
      this.shippingComponent.valid &&
      this.invoiceComponent.valid &&
      this.infoComponent.valid
    );
  }

  onSubmit(): void {
    const salesorders: APIVerkoopordersToWrite = this.makeAPIVerkoopordersToWrite();

    this.isLoading = true;
    this.orderError = undefined;
    this.service.postAPIVerkoopordersToWrite(salesorders).subscribe(
      (apiResponse: ApiResponse) => {
        this.apiResponse = apiResponse;
        this.isLoading = false;
      },
      (error: HttpErrorResponse) => {
        console.error('Failed to submit the salesorder to the backend', error);
        this.isLoading = false;
        this.orderError = error;
      }
    );
  }

  makeAPIVerkoopordersToWrite(): APIVerkoopordersToWrite {
    return {
      ids: this.ids,
      verkooporder: {
        orderDetails: {
          customer: {
            invoiceEmailAddress: this.invoiceComponent.getEmailAddress(),
            userUUID: this.shippingComponent.getUserUUID(),
          },
          customerRef: this.infoComponent.getOrderReference(),
          shippingMethod: SHIPPING_METHOD_CODES[this.shippingComponent.getShippingMethod()],
          // Omit rather than send '', so an empty field reads as standard delivery without relying on
          // Jackson coercing the empty string to null.
          fixedDeliveryDate: this.infoComponent.getFixedDeliveryDate() || undefined,
          deliveryAddress: this.shippingComponent.getDeliveryAddress(),
          orderlines: this.orderlinesComponent.getOrderlines(),
        },
        reason: this.infoComponent.getReason(),
        assessmentNotes: this.infoComponent.getAssessmentNotes(),
        createdByHint: this.profile.fullName,
      },
    };
  }

  hideOrderError(): void {
    this.orderError = undefined;
  }

  markVerkoopordersAsManuallyCreated(): void {
    if (!confirm('Weet je zeker dat je deze verkooporder(s) wil markeren als handmatig aangemaakt?')) {
      return;
    }
    this.manuallyCreatedIsLoading = true;
    const request: ManuallyCreatedRequest = {
      ids: this.ids,
      updatedByHint: this.profile.fullName,
    };
    this.service
      .markVerkoopordersAsManuallyCreated(request)
      .pipe(finalize(() => (this.manuallyCreatedIsLoading = false)))
      .subscribe({
        next: () => {
          this.updateFeedback(
            `Gelukt! De verkooporder${this.ids.length > 1 ? 's zijn' : ' is'} gemarkeerd als handmatig aangemaakt.`, //
            FeedbackType.success,
            false,
            true,
            'Terug naar het overzicht'
          );
        },
        error: err => {
          console.error('Failed to mark verkooporders as manually created', err);
          this.updateFeedback('Er is iets misgegaan', FeedbackType.error, false, true, 'Terug naar het overzicht');
        },
      });
  }

  markUserUUIDAsPreferred(selectedUUID: string): void {
    const verkoopordersCustomerPreferencesToWrite: VerkoopordersCustomerPreferencesToWrite = {
      preferredUserUUID: selectedUUID,
      createdOrUpdatedByHint: this.profile.fullName,
    };

    this.service
      .setVerkoopordersCustomerPreferences(this.indiCustomer.customerNumber, verkoopordersCustomerPreferencesToWrite)
      .subscribe({
        error: (error: Error) => {
          console.error('Failed to set the verkoopordersCurstomerPreferences:', error);
        },
      });
  }

  updateFeedback(message: string, type: FeedbackType, closeButton: boolean, show: boolean, buttonText?: string) {
    this.feedbackSettings.message = message;
    this.feedbackSettings.type = type;
    this.feedbackSettings.closeButton = closeButton;
    this.feedbackSettings.show = show;
    this.feedbackSettings.buttonText = buttonText;
  }
  subscribeToComponentsReady() {
    const allLists = [
      this.orderlinesComponentList,
      this.shippingComponentList,
      this.invoiceComponentList,
      this.infoComponentList,
    ];

    allLists.forEach(componentList => {
      this.subs.add(
        componentList.changes.subscribe(() => {
          this.checkAllReady();
        })
      );
    });

    this.checkAllReady();
  }

  checkAllReady(): void {
    const haveOrderlines = this.orderlinesComponentList.length > 0;
    const haveShipping = this.shippingComponentList.length > 0;
    const haveInvoice = this.invoiceComponentList.length > 0;
    const haveInfo = this.infoComponentList.length > 0;

    if (haveOrderlines && haveShipping && haveInvoice && haveInfo) {
      this.onAllChildrenReady();
    }
  }

  onAllChildrenReady(): void {
    this.orderlinesComponent = this.orderlinesComponentList.get(0) as unknown as NewSalesorderOrderlinesComponent;
    this.shippingComponent = this.shippingComponentList.get(0) as unknown as NewSalesorderShippingDetailsComponent;
    this.invoiceComponent = this.invoiceComponentList.get(0) as unknown as NewSalesorderInvoiceDetailsComponent;
    this.infoComponent = this.infoComponentList.get(0) as unknown as NewSalesorderInfoComponent;

    this.shippingComponent.initializeAddressData();

    setTimeout(() => {
      this.invoiceComponent.invoiceAddress = this.defaultInvoiceAddress;
      this.viewInitialized = true;
    }, 0);
  }

  toOverview(): void {
    void this.router.navigate(['/verkooporders/verkooporder-overzicht']);
  }
}
